import React, { useState, KeyboardEventHandler } from 'react';
import CreatableSelect from 'react-select/creatable';

// Types
import {
  FormTypeType,
  AdjectivesForPropertyQuestionType,
  ReviewFormTemplateType,
} from 'Shared/types/ReviewFormTemplate.type';

// Hooks
import { useQuestion } from './use-question';

// Components
import QuestionWrapper from './QuestionWrapper';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpListSelect } from 'Wpds/components/list';
import { WpText } from 'Wpds/components/text';

type Props = {
  question: AdjectivesForPropertyQuestionType;
  onChange: Function;
  handleDeleteQuestion: Function;
  handleDuplicateQuestion: Function;
  activeQuestionKey: string;
  setActiveQuestionKey: Function;
  handleMoveQuestion: Function;
  reviewFormType: FormTypeType;
  reviewFormTemplates: ReviewFormTemplateType[];
};

const adjectiveMultiSelectComponents = {
  DropdownIndicator: null,
};

const createAdjectiveMultiSelectOption = (label: string) => ({
  label,
  value: label,
});

const AdjectivesForPropertyQuestion = ({
  question,
  onChange,
  handleDeleteQuestion,
  activeQuestionKey,
  setActiveQuestionKey,
  handleDuplicateQuestion,
  handleMoveQuestion,
  reviewFormType,
  reviewFormTemplates,
}: Props) => {
  const baseClassName = 'AdjectivesForPropertyQuestion';
  const { handleUpdateQuestion } = useQuestion({ question, onChange });

  const adjectives = question.adjectives;
  const [adjectiveMultiSelectInputValue, setAdjectiveMultiSelectInputValue] = useState('');

  const isActive = activeQuestionKey === question.key;

  const updateAdjectives = adjectives => {
    handleUpdateQuestion({
      adjectives: { positive: (adjectives || []).map(({ value }) => value), negative: [] },
    });
  };

  const adjectiveMultiSelectValues = [...adjectives.positive, ...adjectives.negative].map(p => ({
    label: p,
    value: p,
  }));

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!adjectiveMultiSelectInputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        updateAdjectives([
          ...adjectiveMultiSelectValues,
          createAdjectiveMultiSelectOption(adjectiveMultiSelectInputValue),
        ]);
        setAdjectiveMultiSelectInputValue('');
        event.preventDefault();
    }
  };

  return (
    <QuestionWrapper
      question={question}
      onChange={handleUpdateQuestion}
      handleDeleteQuestion={handleDeleteQuestion}
      activeQuestionKey={activeQuestionKey}
      setActiveQuestionKey={setActiveQuestionKey}
      handleDuplicateQuestion={handleDuplicateQuestion}
      handleMoveQuestion={handleMoveQuestion}
      reviewFormType={reviewFormType}
      reviewFormTemplates={reviewFormTemplates}
    >
      {isActive ? (
        <div className={baseClassName}>
          <div className="mb-1">Attributes</div>
          <WpText variant="bodyXs">Press &quot;Enter&quot; after each new attribute</WpText>
          <CreatableSelect
            components={adjectiveMultiSelectComponents}
            inputValue={adjectiveMultiSelectInputValue}
            isClearable={false}
            isMulti
            menuIsOpen={false}
            onChange={newValue => updateAdjectives(newValue)}
            onInputChange={newValue => setAdjectiveMultiSelectInputValue(newValue)}
            onKeyDown={handleKeyDown}
            placeholder="Type an attribute and press enter to save it"
            value={adjectiveMultiSelectValues}
          />
        </div>
      ) : (
        <div className={baseClassName}>
          <div className={`${baseClassName}__not-active`}>
            <p className={`${baseClassName}__question`}>
              <span className={`${baseClassName}__question__unbold`}>Select up to 2</span>
            </p>
            <div className={`${baseClassName}__adjective-list`}>
              <WpListSelect
                id={`${question.key}-list-example`}
                selectedOptions={[]}
                options={[...adjectives.positive, ...adjectives.negative]}
                direction="horizontal"
                max={1}
                showIcon={false}
                disableOnMax={false}
              />
            </div>
            <p className={`${baseClassName}__question`}>Why did you choose those attributes?</p>
            <WpTextField
              id="rating-explanation-placeholder"
              label="explanation"
              placeholder="Short Answer"
              labelHidden
              readOnly
              multiline={2}
            />
          </div>
        </div>
      )}
    </QuestionWrapper>
  );
};

export default AdjectivesForPropertyQuestion;
